import { RevenueHistoryData, View, RevenueItemAggregation, RevenueItem } from './types';
import { toDate, withinPeriod, periodLabel, addPeriod, getTotalRevenue } from './utils';
import { getItemTotalRevenue } from '../../utils/revenueAddOns';

export { periodLabel };

export function singleItemTimelineAgg(
  itemData: RevenueHistoryData[],
  itemName: string,
  start: Date,
  view: View,
  valueExtractor: (item: RevenueItem) => number
): { date: string; value: number; isSelected: boolean }[] {
  if (view === 'all') {
    const valueByDate = new Map<string, number>();
    const filteredData = itemData.filter(r => withinPeriod(toDate(r.date), start, view));
    
    filteredData.forEach(r => {
      const date = toDate(r.date);
      const dateKey = date.toISOString().split('T')[0];
      
      r.revenueData.categories.forEach(category => {
        category.items
          .filter(item => item.name === itemName)
          .forEach(item => {
            const value = valueExtractor(item);
            if (valueByDate.has(dateKey)) {
              valueByDate.set(dateKey, valueByDate.get(dateKey)! + value);
            } else {
              valueByDate.set(dateKey, value);
            }
          });
      });
    });
    
    return Array.from(valueByDate.entries())
      .map(([dateKey, value]) => ({
        date: `${dateKey}T12:00:00.000Z`,
        value,
        isSelected: true
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }
  
  // Calculate contextual periods
  let contextualPeriods: { start: Date; end: Date; isSelected: boolean }[] = [];
  
  if (view === 'day') {
    for (let i = -7; i <= 7; i++) {
      const periodStart = addPeriod(start, 'day', i);
      const periodEnd = addPeriod(periodStart, 'day', 1);
      contextualPeriods.push({
        start: periodStart,
        end: periodEnd,
        isSelected: i === 0
      });
    }
  } else if (view === 'week') {
    for (let i = -1; i <= 1; i++) {
      const periodStart = addPeriod(start, 'week', i);
      const periodEnd = addPeriod(periodStart, 'week', 1);
      contextualPeriods.push({
        start: periodStart,
        end: periodEnd,
        isSelected: i === 0
      });
    }
  } else if (view === 'month') {
    for (let i = -1; i <= 1; i++) {
      const periodStart = addPeriod(start, 'month', i);
      const periodEnd = addPeriod(periodStart, 'month', 1);
      contextualPeriods.push({
        start: periodStart,
        end: periodEnd,
        isSelected: i === 0
      });
    }
  } else if (view === 'quarter') {
    for (let i = -1; i <= 1; i++) {
      const periodStart = addPeriod(start, 'quarter', i);
      const periodEnd = addPeriod(periodStart, 'quarter', 1);
      contextualPeriods.push({
        start: periodStart,
        end: periodEnd,
        isSelected: i === 0
      });
    }
  }

  // First check if there's data in the selected period
  const selectedPeriodData = itemData.filter(r => {
    const date = toDate(r.date);
    return date >= start && date < addPeriod(start, view, 1);
  });
  
  const hasSelectedPeriodData = selectedPeriodData.some(r => 
    r.revenueData.categories.some(category => 
      category.items.some(item => item.name === itemName)
    )
  );
  
  // If no data in selected period, return empty array
  if (!hasSelectedPeriodData) {
    return [];
  }
  
  const valueByDate = new Map<string, { value: number; isSelected: boolean }>();
  
  contextualPeriods.forEach(({ start: periodStart, end: periodEnd, isSelected }) => {
    const periodData = itemData.filter(r => {
      const date = toDate(r.date);
      return date >= periodStart && date < periodEnd;
    });
    
    periodData.forEach(r => {
      const date = toDate(r.date);
      const dateKey = date.toISOString().split('T')[0];
      
      r.revenueData.categories.forEach(category => {
        category.items
          .filter(item => item.name === itemName)
          .forEach(item => {
            const value = valueExtractor(item);
            if (valueByDate.has(dateKey)) {
              const existing = valueByDate.get(dateKey)!;
              valueByDate.set(dateKey, {
                value: existing.value + value,
                isSelected: existing.isSelected || isSelected
              });
            } else {
              valueByDate.set(dateKey, { value, isSelected });
            }
          });
      });
    });
  });
  
  return Array.from(valueByDate.entries())
    .map(([dateKey, { value, isSelected }]) => ({
      date: `${dateKey}T12:00:00.000Z`,
      value,
      isSelected
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function revenueTimelineAgg(
  revenueHistory: RevenueHistoryData[] = [],
  start: Date,
  view: View,
  showExVat: boolean = true,
  useItemSums: boolean = false
): { date: string; revenue: number; isSelected: boolean }[] {
  
  if (view === 'all') {
    // For 'all' view, keep existing behavior
    const filteredRevenue = revenueHistory.filter(r => withinPeriod(toDate(r.date), start, view));
    
    const revenueByDate = new Map<string, number>();
    
    filteredRevenue.forEach(r => {
      const date = toDate(r.date);
      const dateKey = date.toISOString().split('T')[0];
      const total = getTotalRevenue(r, showExVat, useItemSums);
      
      if (revenueByDate.has(dateKey)) {
        revenueByDate.set(dateKey, revenueByDate.get(dateKey)! + total);
      } else {
        revenueByDate.set(dateKey, total);
      }
    });
    
    const dataPoints = Array.from(revenueByDate.entries()).map(([dateKey, revenue]) => ({
      date: `${dateKey}T12:00:00.000Z`,
      revenue,
      isSelected: true
    }));
    
    return dataPoints.sort((a, b) => a.date.localeCompare(b.date));
  }
  
  // Calculate contextual periods
  let contextualPeriods: { start: Date; end: Date; isSelected: boolean }[] = [];
  
  if (view === 'day') {
    // For day view: show 7 days before + selected day + 7 days after (if available)
    for (let i = -7; i <= 7; i++) {
      const periodStart = addPeriod(start, 'day', i);
      const periodEnd = addPeriod(periodStart, 'day', 1);
      contextualPeriods.push({
        start: periodStart,
        end: periodEnd,
        isSelected: i === 0
      });
    }
  } else if (view === 'week') {
    // For week view: show previous week + selected week + next week (if data available)
    for (let i = -1; i <= 1; i++) {
      const periodStart = addPeriod(start, 'week', i);
      const periodEnd = addPeriod(periodStart, 'week', 1);
      contextualPeriods.push({
        start: periodStart,
        end: periodEnd,
        isSelected: i === 0
      });
    }
  } else if (view === 'month') {
    // For month view: show previous month + selected month + next month (if data available)
    for (let i = -1; i <= 1; i++) {
      const periodStart = addPeriod(start, 'month', i);
      const periodEnd = addPeriod(periodStart, 'month', 1);
      contextualPeriods.push({
        start: periodStart,
        end: periodEnd,
        isSelected: i === 0
      });
    }
  } else if (view === 'quarter') {
    // For quarter view: show previous quarter + selected quarter + next quarter (if data available)
    for (let i = -1; i <= 1; i++) {
      const periodStart = addPeriod(start, 'quarter', i);
      const periodEnd = addPeriod(periodStart, 'quarter', 1);
      contextualPeriods.push({
        start: periodStart,
        end: periodEnd,
        isSelected: i === 0
      });
    }
  }
  
  // First check if there's data in the selected period
  const selectedPeriodRevenue = revenueHistory.filter(r => {
    const date = toDate(r.date);
    return date >= start && date < addPeriod(start, view, 1);
  });
  
  // If no data in selected period, return empty array
  if (selectedPeriodRevenue.length === 0) {
    return [];
  }
  
  // Group revenue by date for all contextual periods
  const revenueByDate = new Map<string, { revenue: number; isSelected: boolean }>();
  
  contextualPeriods.forEach(({ start: periodStart, end: periodEnd, isSelected }) => {
    const periodRevenue = revenueHistory.filter(r => {
      const date = toDate(r.date);
      return date >= periodStart && date < periodEnd;
    });
    
    periodRevenue.forEach(r => {
      const date = toDate(r.date);
      const dateKey = date.toISOString().split('T')[0];
      const total = getTotalRevenue(r, showExVat, useItemSums);
      
      if (revenueByDate.has(dateKey)) {
        const existing = revenueByDate.get(dateKey)!;
        revenueByDate.set(dateKey, {
          revenue: existing.revenue + total,
          isSelected: existing.isSelected || isSelected // If any period is selected, mark as selected
        });
      } else {
        revenueByDate.set(dateKey, { revenue: total, isSelected });
      }
    });
  });
  
  // Convert map to array and sort by date
  const dataPoints = Array.from(revenueByDate.entries()).map(([dateKey, { revenue, isSelected }]) => ({
    date: `${dateKey}T12:00:00.000Z`,
    revenue,
    isSelected
  }));
  
  return dataPoints.sort((a, b) => a.date.localeCompare(b.date));
}



export function topRevenueItemsAgg(
  revenueHistory: RevenueHistoryData[] = [],
  start: Date,
  view: View,
  showExVat: boolean = true
): RevenueItemAggregation[] {
  const map = new Map<string, RevenueItemAggregation>();
  
  revenueHistory.forEach(r => {
    if (!withinPeriod(toDate(r.date), start, view)) return;
    
    r.revenueData.categories.forEach(category => {
      category.items.forEach(item => {
        const key = `${item.name}-${category.name}`;
        
        if (!map.has(key)) {
          map.set(key, {
            name: item.name,
            quantity: 0,
            revenue: 0,
            category: category.name,
            price: 0,
          });
        }
        
        const agg = map.get(key)!;
        agg.quantity += (item.quantity || 0);
        // Sum the per-item TOTAL revenue (base + nested choices) — choice revenue
        // no longer lives in a separate Add-Ons category, so summing item.exVat
        // alone would undercount Cashdesk BYO dishes.
        agg.revenue += getItemTotalRevenue(item, showExVat);
        // Calculate per-item price based on revenue/quantity for the aggregated total
        agg.price = agg.quantity > 0 ? agg.revenue / agg.quantity : 0;
      });
    });
  });
  
  return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
}

export function revenueInPeriod(
  revenueHistory: RevenueHistoryData[] = [],
  start: Date,
  view: View,
  customStartDate?: Date | null,
  customEndDate?: Date | null
): RevenueHistoryData[] {
  return revenueHistory
    .filter(r => withinPeriod(toDate(r.date), start, view, customStartDate, customEndDate))
    .sort((a, b) => +toDate(b.date) - +toDate(a.date));
}